import React, { useState, useRef } from "react";
import axios from "axios";
import { Mic, Square, Play, Pause, Pencil, Mail, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Expandable text/voice response panel that lives below the post-reading journal prompts.
 * - Text mode: simple textarea
 * - Voice mode: in-browser MediaRecorder (audio-only, webm). Playback in-page.
 *   For now the audio itself is NOT emailed (object-storage upload pipeline is a Phase-2),
 *   but the duration is included in the keepsake email as a breadcrumb.
 * - "Send it to me" emails the text response back as a small keepsake via Resend.
 *   If Resend isn't configured yet, the response is still saved server-side and the UI
 *   shows a polite "we've kept this — email will land once mail is wired" message.
 */
export default function JournalCaptureBox({ prompts, readingExcerpt, cafeSlug, lens, defaultEmail }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("text"); // 'text' | 'voice'
  const [text, setText] = useState("");
  const [selectedPromptIdx, setSelectedPromptIdx] = useState(0);
  const [email, setEmail] = useState(defaultEmail || "");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { saved, emailed, note }
  const [error, setError] = useState(null);

  // voice
  const [isRecording, setIsRecording] = useState(false);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recordedSeconds, setRecordedSeconds] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const streamRef = useRef(null);
  const audioRef = useRef(null);

  const toggleRecord = async () => {
    if (!isRecording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        const mr = new MediaRecorder(stream);
        chunksRef.current = [];
        mr.ondataavailable = (e) => chunksRef.current.push(e.data);
        mr.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          setRecordedUrl(URL.createObjectURL(blob));
          setRecordedSeconds(Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000)));
          streamRef.current?.getTracks().forEach((t) => t.stop());
          transcribeBlob(blob);
        };
        recorderRef.current = mr;
        startedAtRef.current = Date.now();
        mr.start();
        setIsRecording(true);
      } catch (err) {
        setError("Couldn't access microphone — check browser permissions.");
      }
    } else {
      recorderRef.current?.stop();
      setIsRecording(false);
    }
  };

  const transcribeBlob = async (blob) => {
    setTranscribing(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result).split(",")[1] || "");
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const { data } = await axios.post(`${API}/transcribe`, { audio_base64: base64, file_ext: "webm" });
      if (data.text) setText((prev) => (prev && prev.trim() ? prev : data.text));
    } catch {
      // transcription is best-effort — user can still type
    } finally {
      setTranscribing(false);
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      audioRef.current.play();
      setIsPlaying(true);
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const send = async () => {
    setError(null);
    setResult(null);
    if (!email.trim()) { setError("Drop in the email you want me to send this to."); return; }
    if (!text.trim() && !recordedUrl) { setError("Write something or record something — the keepsake needs a body."); return; }
    setSubmitting(true);
    try {
      const prompt = prompts && prompts.length ? prompts[selectedPromptIdx] : null;
      const body = {
        email: email.trim(),
        response_text: text.trim() || "(voice memo, no text written)",
        prompt: prompt || undefined,
        reading_excerpt: readingExcerpt || undefined,
        cafe_slug: cafeSlug || undefined,
        lens: lens || undefined,
        voice_note_seconds: recordedSeconds || undefined,
      };
      const { data } = await axios.post(`${API}/journal/email`, body);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Something hiccuped — try again?");
    } finally {
      setSubmitting(false);
    }
  };

  if (result && (result.saved || result.emailed)) {
    return (
      <div data-testid="journal-capture-confirm" style={confirmStyle}>
        <Check size={16} style={{ verticalAlign: "middle", marginRight: 8, color: "var(--gold)" }} />
        <span>
          {result.emailed ? (
            <>Sent to <strong style={{ color: "var(--cream)" }}>{email}</strong>. Check your inbox — the cup remembers.</>
          ) : (
            <>Saved to the vault. {result.note || "We'll send it the moment email goes live."}</>
          )}
        </span>
      </div>
    );
  }

  return (
    <div data-testid="journal-capture-box" style={{ marginTop: 26 }}>
      {!open ? (
        <button
          type="button"
          data-testid="journal-capture-open"
          onClick={() => setOpen(true)}
          className="mono-btn"
          style={openCtaStyle}
        >
          <Pencil size={11} style={{ verticalAlign: "middle", marginRight: 8 }} />
          your turn — write or speak back
        </button>
      ) : (
        <div style={panelStyle} role="region" aria-label="Journal response">
          {/* Prompt chips */}
          {prompts && prompts.length > 1 && (
            <div data-testid="journal-capture-prompt-chips" style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
              {prompts.slice(0, 3).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`journal-capture-chip-${i}`}
                  onClick={() => setSelectedPromptIdx(i)}
                  className={`mono-btn ${selectedPromptIdx === i ? "active" : ""}`}
                  style={{ padding: "4px 9px", fontSize: 9 }}
                >
                  prompt {i + 1}
                </button>
              ))}
            </div>
          )}
          {prompts && prompts[selectedPromptIdx] && (
            <p style={{ margin: "0 0 14px", fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 15, color: "var(--cream-soft)", lineHeight: 1.5, borderLeft: "2px solid var(--terracotta-soft)", paddingLeft: 12 }}>
              {prompts[selectedPromptIdx]}
            </p>
          )}

          {/* Mode tabs */}
          <div role="tablist" style={{ display: "flex", gap: 6, marginBottom: 12 }} data-testid="journal-capture-mode-tabs">
            <button
              type="button"
              role="tab"
              data-testid="journal-capture-mode-text"
              onClick={() => setMode("text")}
              className={`mono-btn ${mode === "text" ? "active" : ""}`}
              style={{ flex: 1 }}
            >
              <Pencil size={10} style={{ verticalAlign: "middle", marginRight: 6 }} />
              write
            </button>
            <button
              type="button"
              role="tab"
              data-testid="journal-capture-mode-voice"
              onClick={() => setMode("voice")}
              className={`mono-btn ${mode === "voice" ? "active" : ""}`}
              style={{ flex: 1 }}
            >
              <Mic size={10} style={{ verticalAlign: "middle", marginRight: 6 }} />
              speak
            </button>
          </div>

          {mode === "text" ? (
            <textarea
              data-testid="journal-capture-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="what's coming up for you?"
              maxLength={4000}
              style={textareaStyle}
            />
          ) : (
            <div data-testid="journal-capture-voice" style={voiceWrapStyle}>
              {!recordedUrl ? (
                <button
                  type="button"
                  data-testid="journal-capture-record"
                  onClick={toggleRecord}
                  className="mono-btn"
                  style={{ ...recordBtnStyle, background: isRecording ? "var(--rust)" : "transparent", color: isRecording ? "var(--cream)" : "var(--cream-soft)" }}
                >
                  {isRecording ? <Square size={12} /> : <Mic size={12} />}
                  <span style={{ marginLeft: 8 }}>
                    {isRecording ? "stop · recording…" : "tap to record"}
                  </span>
                </button>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center" }}>
                  <button type="button" data-testid="journal-capture-play" onClick={togglePlay} className="mono-btn" style={recordBtnStyle}>
                    {isPlaying ? <Pause size={12} /> : <Play size={12} />}
                    <span style={{ marginLeft: 8 }}>{recordedSeconds}s memo</span>
                  </button>
                  <button type="button" data-testid="journal-capture-rerecord" onClick={() => { setRecordedUrl(null); setRecordedSeconds(0); }} className="mono-btn" style={{ padding: "9px 12px", fontSize: 9 }}>
                    re-record
                  </button>
                  <audio ref={audioRef} src={recordedUrl} onEnded={() => setIsPlaying(false)} />
                </div>
              )}
              <p style={{ margin: "12px 0 0", fontSize: 11, color: "var(--dim)", fontStyle: "italic", fontFamily: "'IM Fell DW Pica', serif", textAlign: "center", lineHeight: 1.4 }}>
                {transcribing
                  ? "transcribing your voice…"
                  : "We transcribe your voice to text so it can be emailed to you as a keepsake — edit it below if you like."}
              </p>
              <textarea
                data-testid="journal-capture-voice-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="(your transcript will appear here — or jot a line yourself)"
                maxLength={4000}
                style={{ ...textareaStyle, marginTop: 10, minHeight: 70 }}
              />
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <label htmlFor="journal-capture-email" style={{ display: "block", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--terracotta-soft)", marginBottom: 6 }}>
              send it to my inbox
            </label>
            <input
              id="journal-capture-email"
              data-testid="journal-capture-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yours.com"
              style={inputStyle}
              autoComplete="email"
            />
          </div>

          {error && <p data-testid="journal-capture-error" className="error-banner" style={{ marginTop: 12 }}>{error}</p>}

          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button
              type="button"
              data-testid="journal-capture-send"
              onClick={send}
              disabled={submitting}
              className="primary-btn"
              style={{ flex: 1, padding: "14px 18px", fontSize: 14 }}
            >
              {submitting ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Loader2 size={14} className="spin" /> sending…
                </span>
              ) : (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Mail size={14} /> send it to me
                </span>
              )}
            </button>
            <button
              type="button"
              data-testid="journal-capture-close"
              onClick={() => { setOpen(false); setError(null); }}
              className="mono-btn"
              style={{ padding: "14px 14px", fontSize: 10 }}
            >
              close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// -------- styles (kept local because this is a one-off) --------
const openCtaStyle = {
  width: "100%",
  padding: "14px 16px",
  border: "1px dashed rgba(143, 196, 224, 0.35)",
  background: "rgba(143, 196, 224, 0.04)",
  color: "var(--cream)",
  fontFamily: "'Space Mono', monospace",
  fontSize: 11,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  cursor: "pointer",
};
const panelStyle = {
  padding: "20px 18px 18px",
  background: "rgba(143, 196, 224, 0.04)",
  border: "1px solid rgba(143, 196, 224, 0.22)",
};
const textareaStyle = {
  width: "100%",
  minHeight: 110,
  padding: "12px 14px",
  background: "rgba(5, 11, 27, 0.55)",
  border: "1px solid rgba(143, 196, 224, 0.22)",
  color: "var(--cream)",
  fontFamily: "'IM Fell DW Pica', serif",
  fontSize: 16,
  fontStyle: "italic",
  lineHeight: 1.5,
  resize: "vertical",
  outline: "none",
  boxSizing: "border-box",
};
const inputStyle = {
  width: "100%",
  padding: "11px 14px",
  background: "rgba(5, 11, 27, 0.55)",
  border: "1px solid rgba(143, 196, 224, 0.22)",
  color: "var(--cream)",
  fontFamily: "'Space Mono', monospace",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};
const voiceWrapStyle = {
  padding: "16px",
  border: "1px solid rgba(143, 196, 224, 0.18)",
  background: "rgba(5, 11, 27, 0.35)",
};
const recordBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "10px 16px",
  fontFamily: "'Space Mono', monospace",
  fontSize: 11,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
};
const confirmStyle = {
  marginTop: 22,
  padding: "16px 18px",
  border: "1px solid var(--gold)",
  background: "rgba(212, 200, 150, 0.08)",
  color: "var(--cream-soft)",
  fontFamily: "'IM Fell DW Pica', serif",
  fontStyle: "italic",
  fontSize: 15,
  lineHeight: 1.5,
};
